import { formatFractionDisplay, isFeatureActiveOnCurrentFarm } from "utils";

import { FEATURE_FLAG_SLUGS } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  ActivePracticeBundle,
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  CarbonProgramResult,
  CarbonRunnerErrorType,
  CropFCPO,
  ENROLLMENT_TYPE,
  FieldInfoWithError,
  FieldOutcomesByPractice,
  FieldPracticeResponse,
  PRACTICE_SLUG,
  PracticeBundle,
  PracticeCrop,
  PracticeFCPO,
  PracticeFertilizerOrganic,
  PracticeOutcome,
  PracticeSlug,
  PracticeWithError,
  SingleFieldOutcomes,
  ValidAggregation,
  ValidOrInvalidAggregation,
} from "models";

export const isSingleFieldOutcomes = (value?: CarbonProgramResult): value is SingleFieldOutcomes => !!value && "fieldOutcomes" in value;

export const isValidAggregation = (aggregation?: ValidOrInvalidAggregation): aggregation is ValidAggregation => !!aggregation?.validation?.valid;

export type FieldContributionStatus = {
  fieldsContributingToPayouts: number;
  fieldsWithNonFatalErrors: number;
  totalFields: number;
};

export const isFieldEnrolled = (practiceBundles: PracticeBundle[], carbonProgram: CarbonProgram | null): boolean => {
  if (!carbonProgram) return false;

  const requiredPractices = carbonProgram?.fieldsManagement.practices?.filter((practice) => practice.required);
  if (!requiredPractices || requiredPractices.length === 0) return false;

  // If any practice has previously been activated, the field is enrolled
  return requiredPractices.some((practice) => {
    const bundle = practiceBundles?.find((bundle) => bundle.slug === practice.slug);
    return !!bundle?.active && !bundle?.discontinuedYear;
  });
};

// inventory programs enroll all fields automatically, unless they explicitly opt into user enrollment
export const isAutoEnrolledInventoryProgram = (carbonProgram: CarbonProgram | null | undefined): boolean =>
  carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory && carbonProgram.fieldsManagement.fieldEnrollmentType !== ENROLLMENT_TYPE.user;

export type FieldParticipationStatus = "rejected" | "reactivated";

/**
 * Derived from the crops bundle. A cancelled reactivation has both years set and counts as rejected,
 * so a field never has both statuses at once. A reactivation from an earlier reporting year is settled,
 * so that field has no status and shows like any other field.
 */
export const getFieldParticipationStatus = (
  practiceBundles: PracticeBundle[],
  activeFarmReportingYear: number | undefined
): FieldParticipationStatus | null => {
  const cropBundle = practiceBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.CROPS);

  if (Number.isInteger(cropBundle?.discontinuedYear)) return "rejected";
  if (Number.isInteger(cropBundle?.reactivatedYear) && cropBundle?.reactivatedYear === activeFarmReportingYear) return "reactivated";
  return null;
};

export const canCancelFieldReactivation = (practiceBundles: PracticeBundle[], activeFarmReportingYear: number | undefined): boolean =>
  getFieldParticipationStatus(practiceBundles, activeFarmReportingYear) === "reactivated";

/**
 * Determines if a field can be deleted, if
 * - The carbon program is an inventory program with automatic field enrollment (where fields can be deleted even if enrolled)
 * - OR the field is not enrolled (intervention programs and inventory programs with user enrollment)
 */
export const canDeleteField = ({
  availablePracticeBundles,
  carbonProgram,
}: {
  availablePracticeBundles?: PracticeBundle[] | undefined;
  carbonProgram?: CarbonProgram | null;
}): boolean => {
  if (isAutoEnrolledInventoryProgram(carbonProgram)) return true;

  const enrolled = isFieldEnrolled(availablePracticeBundles ?? [], carbonProgram ?? null);
  return !enrolled;
};

export const hasActiveCropRotation = (
  fieldPractices?:
    | {
        activePracticeBundles?: ActivePracticeBundle[];
        practices?: PracticeFCPO[];
        crops?: CropFCPO[];
        fieldHasFailedVerification?: boolean;
      }
    | FieldPracticeResponse
    | undefined
): boolean => {
  if (!fieldPractices) return false;

  if ("activePracticeBundles" in fieldPractices) {
    return (fieldPractices?.activePracticeBundles && !!fieldPractices?.activePracticeBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.CROPS)) || false;
  }

  if ("availablePracticeBundles" in fieldPractices) {
    return (
      (fieldPractices?.availablePracticeBundles &&
        !!fieldPractices?.availablePracticeBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.CROPS && bundle.active)) ||
      false
    );
  }

  return false;
};

export const fieldHasActivePractices = (
  fieldPractices?:
    | {
        availablePracticeBundles: ActivePracticeBundle[];
        practices: PracticeFCPO[];
        crops: CropFCPO[];
        fieldHasFailedVerification: boolean;
      }
    | FieldPracticeResponse
    | undefined
): boolean => !!fieldPractices?.availablePracticeBundles.some((bundle) => bundle.active);

export const areFieldOutcomesValidForYear = (fieldOutcomes: FieldOutcomesByPractice, year: number): boolean =>
  Object.values(fieldOutcomes || {})
    .filter(({ isParticipating }) => isParticipating)
    .every((outcome) =>
      Object.entries(outcome.years).every(
        ([yearKey, yearlyData]) => yearlyData.validation?.valid || (!yearlyData.isBaselineYear && yearKey !== year.toString())
      )
    );

export const isNextYearOutcomeValid = (fieldOutcomes: FieldOutcomesByPractice, reportingYear: number, carbonProgram: CarbonProgram | null): boolean => {
  const nextYear = reportingYear + 1;

  return Object.entries(fieldOutcomes || {})
    .filter(([slug]) => (carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.intervention ? slug !== PRACTICE_SLUG.CROPS : true))
    .every(([_slug, practice]) => (practice.isParticipating ? practice.years?.[nextYear]?.validation?.valid : true));
};

const checkPracticeDependency = (calculation: SingleFieldOutcomes, practiceSlug: PracticeSlug, year: number) =>
  calculation?.fieldOutcomes?.[practiceSlug]?.years?.[year]?.validation?.valid ?? false;

export const isPracticeDependencyCompleted = (calculation: SingleFieldOutcomes, practiceSlug: PracticeSlug, year: number): boolean => {
  switch (practiceSlug) {
    case PRACTICE_SLUG.CROPS:
      return true;
    case PRACTICE_SLUG.MINERAL_FERTILIZERS:
      return checkPracticeDependency(calculation, PRACTICE_SLUG.ORGANIC_FERTILIZERS, year);
    case PRACTICE_SLUG.ORGANIC_FERTILIZERS:
      return checkPracticeDependency(calculation, PRACTICE_SLUG.MINERAL_FERTILIZERS, year);
    case PRACTICE_SLUG.TILLAGES:
      return checkPracticeDependency(calculation, PRACTICE_SLUG.SEEDINGS, year);
    case PRACTICE_SLUG.SEEDINGS:
      return checkPracticeDependency(calculation, PRACTICE_SLUG.TILLAGES, year);
    default:
      return false;
  }
};

export const isPracticeEnabledByBundle = (practiceSlug: PracticeSlug, practiceBundles?: PracticeBundle[]): boolean => {
  if (!practiceBundles) return false;

  return practiceBundles.find((bundle) => bundle?.slug === practiceSlug)?.active ?? false;
};

export interface FarmFootprintingCapabilities {
  readonly dairyEnabled: boolean;
  readonly beefEnabled: boolean;
  readonly porkEnabled: boolean;
  readonly arableEnabled: boolean;
}

export const getFarmFootprintingCapabilities = (carbonProgram?: CarbonProgram): FarmFootprintingCapabilities => ({
  dairyEnabled: carbonProgram?.platformFeatures?.footprinting?.dairy === true,
  beefEnabled: carbonProgram?.platformFeatures?.footprinting?.beef === true,
  porkEnabled: carbonProgram?.platformFeatures?.footprinting?.pork === true,
  arableEnabled: (carbonProgram?.farmLevelFootprinting?.programCrops.length ?? 0) > 0,
});

export const footprintingPayoutForYear = (cp: CarbonProgram, year: number) => {
  if (!cp.farmLevelFootprinting) {
    return 0;
  }

  const bonuses = cp.farmLevelFootprinting.formSubmissionBonuses;
  let payout = 0;
  if (bonuses.farm) {
    payout += bonuses.farm[year];
  }

  const cropFactor = bonuses.cropPayouts === "per-crop" ? cp.farmLevelFootprinting?.programCrops.length ?? 0 : 1;
  if (bonuses.crop) {
    payout += bonuses.crop[year] * cropFactor;
  }

  return payout;
};

export const calculateRemovalsProgress = (program: CarbonProgram | null, programOutcomes: CarbonProgramOutcomesResponse | undefined, currentYear: number) => {
  if (!programOutcomes) return 0;

  let validPracticesCount = 0;
  let totalRequiredPractices = 0;

  Object.values(programOutcomes).forEach((outcome) => {
    if ("fieldOutcomes" in outcome) {
      Object.values(outcome.fieldOutcomes).forEach((practice) => {
        if (Object.values(practice.required).some((requirement) => requirement)) {
          totalRequiredPractices += 1;

          const practiceIsValid =
            program?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory
              ? practice.years?.[currentYear]?.validation?.valid
              : outcomesExistForPractice(practice) && Object.values(practice.years).every((aggregation) => aggregation.validation?.valid);

          if (practice.isParticipating && practiceIsValid) {
            validPracticesCount += 1;
          }
        }
      });
    }
  });

  return totalRequiredPractices > 0 ? formatFractionDisplay(validPracticesCount / totalRequiredPractices) : 0;
};

const fieldHasValidPayouts = (fieldOutcome: SingleFieldOutcomes, reportingYear: number): boolean => {
  if (!fieldOutcome?.fieldOutcomes) return false;

  return Object.values(fieldOutcome.fieldOutcomes).some((practiceOutcome) => {
    if (!practiceOutcome?.years) return false;

    const yearData = practiceOutcome.years[reportingYear];
    return yearData && !yearData.isBaselineYear && yearData.validation?.valid && "payouts" in yearData && Number(yearData.payouts?.avg);
  });
};

export const calculateFieldContributionStatus = (carbonProgramOutcomes: CarbonProgramOutcomesResponse, reportingYear: number): FieldContributionStatus => {
  // doing this to get all the fields, not just the single field outcomes without errors
  const carbonProgramResults = Object.values(carbonProgramOutcomes ?? {});

  const totalFields = carbonProgramResults.filter(
    (outcome) => "error" in outcome || (isSingleFieldOutcomes(outcome) && outcome.fieldOutcomes.crops?.isParticipating)
  ).length;

  let fieldsContributingToPayouts = 0;
  carbonProgramResults.filter(isSingleFieldOutcomes).forEach((fieldOutcome) => {
    const isParticipating = fieldOutcome?.fieldOutcomes?.crops?.isParticipating;

    const hasValidPayouts = fieldHasValidPayouts(fieldOutcome, reportingYear);

    // Only count fields that meet both criteria (valid payout data and participating)
    if (isParticipating && hasValidPayouts) {
      fieldsContributingToPayouts += 1;
    }
  });

  // fields for which we could determine a participation and missing data status, but no payout
  const fieldsWithNonFatalErrors = carbonProgramResults.filter(
    (programResult) => programResult && !("error" in programResult) && !outcomesExistOnField(programResult.fieldOutcomes)
  ).length;

  return {
    fieldsContributingToPayouts,
    fieldsWithNonFatalErrors,
    totalFields,
  };
};

export const someOutcomeHasErrorOfType = (outcomesData: CarbonProgramOutcomesResponse, errorType: CarbonRunnerErrorType) =>
  Object.values(outcomesData).some((result) => "error" in result && result.error.message.includes(errorType));

/**
 * Gets the internal name of a carbon program if available, otherwise falls back to the program name.
 * This utility is specifically for admin views to display program names that farmers shouldn't see.
 */
export const getProgramInternalName = (program: CarbonProgram): string => program.internalName || program.programName;

export const isRentenbankProgram = (carbonProgram?: CarbonProgram | null): boolean => {
  if (!carbonProgram) return false;

  const programId = carbonProgram.id;

  return programId === "ad589654-d0de-43a4-8cac-59f9c671192a"; // Rentenbank program ID
};

export const outcomesExistOnField = (fieldOutcomes: FieldOutcomesByPractice | FieldInfoWithError): fieldOutcomes is FieldOutcomesByPractice =>
  !Object.values(fieldOutcomes).some((practice) => practice.isParticipating && "error" in practice);

export const anyYearIsInvalid = (fieldOutcomes: FieldOutcomesByPractice | FieldInfoWithError): fieldOutcomes is FieldOutcomesByPractice =>
  Object.values(fieldOutcomes).some(
    (practice) => !("error" in practice) && Object.values(practice.years).some((aggregation) => !(aggregation as ValidOrInvalidAggregation).validation.valid)
  );

/** Uncapped outcome-based payout of an inventory field for the reporting year, or null when the field doesn't count towards it */
export const getFieldOutcomeBasedPayoutRange = (field: SingleFieldOutcomes, reportingYear: number): { min: number; max: number } | null => {
  const reportingYearAggregation = field.fieldOutcomes.crops?.years?.[reportingYear];

  if (!outcomesExistOnField(field.fieldOutcomes) || anyYearIsInvalid(field.fieldOutcomes) || !isValidAggregation(reportingYearAggregation)) return null;

  return { min: reportingYearAggregation.payouts?.min ?? 0, max: reportingYearAggregation.payouts?.max ?? 0 };
};

/** Practice-based payout of an inventory field, capped to the field's maximum payout */
export const getCappedFieldPracticeBasedPayout = (field: SingleFieldOutcomes): number => {
  const practiceBasedPayout = field.practiceIncentives?.earnedTotalPayout ?? 0;

  return typeof field.potentialPayout === "number" ? Math.min(practiceBasedPayout, field.potentialPayout) : practiceBasedPayout;
};

/** Same range capped to what the field's maximum payout leaves after its practice-based payout, the per-field version of the farm cap */
export const getCappedFieldOutcomeBasedPayoutRange = (field: SingleFieldOutcomes, reportingYear: number): { min: number; max: number } | null => {
  const payoutRange = getFieldOutcomeBasedPayoutRange(field, reportingYear);

  if (!payoutRange || typeof field.potentialPayout !== "number") return payoutRange;

  const remainingPayout = Math.max(0, field.potentialPayout - getCappedFieldPracticeBasedPayout(field));

  return { min: Math.min(payoutRange.min, remainingPayout), max: Math.min(payoutRange.max, remainingPayout) };
};

export const outcomesExistForPractice = (practiceOutcome: PracticeOutcome | PracticeWithError): practiceOutcome is PracticeOutcome =>
  !("error" in practiceOutcome);

// same rule the axios interceptor sends to the API as the `soil-plus-3-data-gap-feature-active` header
export const isSoilPlus3DataGapActiveForProgram = (carbonProgram?: CarbonProgram | null): boolean =>
  !carbonProgram?.platformFeatures?.fieldManagement?.insightsImpactPanel && isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);

const requiresModelReadyEntries = (carbonProgram?: CarbonProgram | null): boolean =>
  carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory || isSoilPlus3DataGapActiveForProgram(carbonProgram);

export const isCropNotModelReady = (crop: Pick<PracticeCrop, "isModelReady"> | undefined, carbonProgram?: CarbonProgram | null): boolean =>
  !!crop && requiresModelReadyEntries(carbonProgram) && !crop.isModelReady;

export const isOrganicFertilizerNotModelReady = (
  fertilizer: Pick<PracticeFertilizerOrganic, "isModelReady"> | undefined,
  carbonProgram?: CarbonProgram | null
): boolean => !!fertilizer && requiresModelReadyEntries(carbonProgram) && !fertilizer.isModelReady;

/**
 * A field produces no payout when a participating practice has no points for the reporting year, e.g. because the program's
 * SOC model can't calculate its crops. Baseline years and years the field isn't accounted for are skipped. Such fields don't block the submission.
 */
export const isFieldWithoutPayoutDueToNoPoints = (result: CarbonProgramResult | undefined, reportingYear: number | undefined): boolean => {
  if (!result || !reportingYear) return false;
  if (!isSingleFieldOutcomes(result)) return true;

  return Object.values(result.fieldOutcomes as FieldOutcomesByPractice | FieldInfoWithError).some((outcome) => {
    if (!outcome?.isParticipating) return false;
    if (!outcomesExistForPractice(outcome)) return true;

    const reportingYearOutcome = outcome.years[reportingYear];
    if (!reportingYearOutcome || reportingYearOutcome.isBaselineYear) return false;
    return !isValidAggregation(reportingYearOutcome) || !reportingYearOutcome.points;
  });
};
